// libraries
import { Control } from 'react-hook-form'
import { Typography, Stack } from '@mui/material'
import { IconButton } from '@mui/material_modern'
import { AddCircleOutline, Edit as EditIcon } from '@mui/icons-material'
import { GridColDef } from '@mui/x-data-grid-pro'

// components
import InputFormat from 'components/generic/inputs/formatting/InputFormat'
import StyledLink from 'components/generic/typography/StyledLink.jsx'
import Input from 'components/generic/form/Input'
import { RemainderCell } from 'components/containers/invoice-payments/components/misc/RemainderCell'

// utils
import { losslessToFixed } from 'utils/helpers'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'

export const getOpenInvoicesColumns = (
  pricePrepend = '$',
  selectedRows: any[],
  openAdjustmentModal: (row: any) => void,
  getValues: (payload?: string | string[]) => any,
  setValue: (name: string, value: unknown) => void,
  control: Control<any>,
): GridColDef[] => [
  {
    field: 'invoiceTicketId',
    headerName: 'Invoice #',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    renderCell: params => {
      return (
        <StyledLink
          to={`/app/sales/invoices/${params.row?.id}`}
          data-testid={`invoice-ticket-id-${params.row?.id}`}
          onClick={event => {
            event.preventDefault()
            window.open(`/app/sales/invoices/${params.row?.id}`, '_blank')
          }}
        >
          {params.row.invoiceTicketId}
        </StyledLink>
      )
    },
    sortable: false,
  },
  {
    field: 'outboundTicketIds',
    headerName: 'Tickets',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    valueGetter: params => {
      return params.row?.outboundTicketIds?.length
    },
    sortable: false,
  },
  {
    field: 'salesOrder.salesOrderNumber',
    headerName: 'SO#',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    valueGetter: params => {
      return params.row.salesOrder?.salesOrderNumber || ''
    },
    sortable: false,
    width: 120,
  },
  {
    field: 'dueDate',
    headerName: 'Due Date',
    flex: 1,
    headerAlign: 'left',
    align: 'left',
    valueGetter: params => {
      return dateFormatter(params.row?.dueDate)
    },
    sortable: false,
    width: 120,
  },
  {
    field: 'amountDue',
    headerName: 'Amount Due',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    valueGetter: params => {
      return `${pricePrepend}${losslessToFixed(Number(params.value), 2)}`
    },
    sortable: false,
  },
  {
    field: 'amountPaid',
    headerName: 'Amount Paid',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: ({ row: { id, amountPaid } }) => {
      const isSelected = selectedRows.includes(id)

      return (
        <Input
          control={control}
          name={`invoices.${id}.amountPaid`}
          defaultValue={amountPaid || amountPaid === 0 ? losslessToFixed(Number(amountPaid), 2) : '0.00'}
          disabled={!isSelected}
          InputProps={{
            inputComponent: InputFormat,
            inputProps: {
              inputType: 'price',
              decimalScale: 2,
              allowNegative: true,
              style: {
                fontSize: '14px',
                textAlign: 'right',
              },
              onBlur: ({ target: { value } }) => {
                if (!value) {
                  setValue(`invoices.${id}.amountPaid`, '0.00')
                }
              },
            },
          }}
          size="small"
          fractionDigits={2}
          data-testid={`amount-paid-input-${id}`}
        />
      )
    },
    sortable: false,
  },
  {
    field: 'adjustment',
    headerName: 'Adjustment',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    renderCell: params => {
      const isSelected = selectedRows.includes(params.row.id)
      const invoiceId = params.row.id
      const adjustment = getValues(`invoices.${invoiceId}`)?.adjustment || 0
      const hasAdjustment = adjustment !== 0

      return (
        <Stack direction="row" alignItems="center" spacing={1} overflow="scroll">
          <IconButton
            disabled={!isSelected}
            size="small"
            onClick={() => openAdjustmentModal(params.row)}
            data-testid={`adjustment-button-${params.row.id}`}
          >
            {hasAdjustment ? (
              <EditIcon color={isSelected ? 'primary' : 'disabled'} fontSize="small" />
            ) : (
              <AddCircleOutline color={isSelected ? 'primary' : 'disabled'} fontSize="small" />
            )}
          </IconButton>
          <Typography
            variant="body2"
            data-testid={`adjustment-value-${params.row.id}`}
          >{`${pricePrepend}${losslessToFixed(adjustment, 2)}`}</Typography>
        </Stack>
      )
    },
    sortable: false,
  },
  {
    field: 'remainder',
    headerName: 'Remainder',
    flex: 1,
    headerAlign: 'right',
    align: 'right',
    sortable: false,
    renderCell: ({ row }) => (
      <RemainderCell control={control} pricePrepend={pricePrepend} id={row.id} amountDue={row.amountDue} />
    ),
  },
]
